import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

export function useWishlist() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["wishlist", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("wishlist_items")
        .select("product_id")
        .order("created_at", { ascending: false });
      if (error) {
        console.error("[wishlist] load failed", error);
        return [];
      }
      return (data ?? []).map((r) => r.product_id);
    },
  });

  const ids = q.data ?? [];
  const set = new Set(ids);

  const toggle = useMutation({
    mutationFn: async (productId: string) => {
      if (!user) throw new Error("Sign in to save items");
      if (set.has(productId)) {
        const { error } = await supabase
          .from("wishlist_items")
          .delete()
          .eq("user_id", user.id)
          .eq("product_id", productId);
        if (error) throw new Error(error.message);
        return { added: false };
      }
      const { error } = await supabase
        .from("wishlist_items")
        .insert({ user_id: user.id, product_id: productId });
      if (error) throw new Error(error.message);
      return { added: true };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["wishlist", user?.id] });
      toast.success(res.added ? "Saved to wishlist" : "Removed from wishlist");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return {
    ids,
    isSaved: (productId: string) => set.has(productId),
    toggle: (productId: string) => toggle.mutate(productId),
    isToggling: toggle.isPending,
    loading: q.isLoading,
    isAuthenticated: !!user,
  };
}
